import { makeStyles } from "tss-react/mui";
import { Box } from "@mui/material";
import { Label, SortedDependencyMap } from "../../autoTrigger.types";
import { ColumnHeader } from "./ColumnHeader";
import { OrderTemplateNode } from "./OrderTemplateNode";
import { TriggerNode } from "./TriggerNode";
import {
  ColumnHeaderNodeData,
  OrderTemplateNodeData,
  TriggerNodeData,
} from "./chartView.types";
import { useChartLayout } from "./useChartLayout";

interface ChartViewProps {
  sortedDependencyMap: SortedDependencyMap;
  labels: Label[];
  orderTemplateName: string | null;
  isNumbered?: boolean;
}

export const ChartView = ({
  sortedDependencyMap,
  labels,
  orderTemplateName,
  isNumbered,
}: ChartViewProps) => {
  const { classes } = useStyles();
  const { nodes, edges, columnLanes, containerWidth } = useChartLayout({
    sortedDependencyMap,
    labels,
    orderTemplateName,
    isNumbered,
  });

  return (
    <Box className={classes.scrollContainer}>
      <Box
        className={classes.canvas}
        style={{
          width: containerWidth,
        }}
      >
        {columnLanes.map((col, i) => (
          <Box
            key={`col-bg-${i}`}
            className={classes.columnBg}
            style={{
              left: col.x,
              width: col.width,
            }}
          />
        ))}

        <svg className={classes.edgeSvg} width={containerWidth} height="100%">
          {edges.map((edge) => (
            <path
              key={edge.id}
              d={edge.path}
              fill="none"
              stroke={edge.color}
              strokeWidth={2}
            />
          ))}
        </svg>

        {nodes.map((node) => (
          <Box
            key={node.id}
            className={classes.node}
            style={{ left: node.x, top: node.y }}
          >
            {node.type === "columnHeader" && (
              <ColumnHeader data={node.data as ColumnHeaderNodeData} />
            )}
            {node.type === "trigger" && (
              <TriggerNode data={node.data as TriggerNodeData} />
            )}
            {node.type === "orderTemplate" && (
              <OrderTemplateNode data={node.data as OrderTemplateNodeData} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()(() => ({
  scrollContainer: {
    width: "100%",
    display: "flex",
    height: "100%",
    overflowX: "auto",
    overflowY: "auto",
  },
  canvas: {
    position: "relative",
    backgroundColor: "#fafafa",
    borderRadius: 8,
  },
  columnBg: {
    position: "absolute",
    top: 0,
    bottom: 0,
    backgroundColor: "#ffffff",
    zIndex: 0,
  },
  edgeSvg: {
    position: "absolute",
    top: 0,
    left: 0,
    pointerEvents: "none",
    overflow: "visible",
    zIndex: 1,
  },
  node: {
    position: "absolute",
    zIndex: 2,
  },
}));
